import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone, EnvelopeSimple, Clock, MapPin, Baby, Books, ForkKnife, Student,
  HeartStraight, Sparkle, ArrowRight, CheckCircle, List, X,
} from "@phosphor-icons/react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { IMG } from "@/lib/images";

const programs = [
  { icon: Baby, title: "Infant & Toddler Care", age: "0 – 3 years", color: "bg-blue-50 text-blue-600",
    text: "Arkansas state-approved Early Learning curriculum from birth to three, with individualized plans tracking every developmental milestone." },
  { icon: Books, title: "Preschool & Pre-K", age: "3 – 5 years", color: "bg-amber-50 text-amber-600",
    text: "Taught by an Arkansas Certified Teacher, preparing children for kindergarten on the Arkansas Frameworks — and making it fun." },
  { icon: Student, title: "After School & Tutoring", age: "5 – 12 years", color: "bg-red-50 text-red-500",
    text: "Snacks, daily homework help and tutoring in reading, math and language so evenings belong to your family again." },
  { icon: ForkKnife, title: "Nutritional Food Program", age: "All ages", color: "bg-emerald-50 text-emerald-600",
    text: "Breakfast, lunch, snacks and dinner following USDA guidelines — milk, meats, breads, fruits and vegetables every day." },
  { icon: HeartStraight, title: "Pediatric Therapy On-Site", age: "All ages", color: "bg-rose-50 text-rose-500",
    text: "With Hands That Help: speech, occupational, physical and developmental therapy, case management and parent training." },
  { icon: Sparkle, title: "Transportation & Outreach", age: "All ages", color: "bg-indigo-50 text-indigo-600",
    text: "Flexible hours, transportation to and from home, before/after school pickup and a community outreach program." },
];

const faqs = [
  { q: "Why should I choose Teach Play to Learn?",
    a: "Our highly educated team keeps your child in a secure atmosphere that fosters social development and education. We emphasize age-appropriate schooling and believe play makes learning easier — clarifying ideas, relieving emotional distress and bringing joy." },
  { q: "What is my role as a parent?",
    a: "Parent involvement is critical to a child's success. We invite parents to stay in communication with our staff and teachers, and we respect the uniqueness of every family we serve." },
  { q: "Will my child be fed?",
    a: "Yes. Your child receives complete, balanced meals through our USDA Nutritional Food Program. Note any allergies or sensitivities on your application and we will accommodate them." },
  { q: "How much do your services cost?",
    a: "Services are priced individually based on the care your family needs. Vouchers and all other forms of payment are accepted. Send us a message for a precise estimate." },
  { q: "How do I contact you?",
    a: "Call (501) 904-7103 during business hours, text (501) 791-1919, or email pprekinder@gmail.com. You can also use the contact form on this page." },
];

const Section = ({ children, className = "", id }) => (
  <section id={id} className={`px-6 sm:px-10 lg:px-16 py-20 lg:py-28 ${className}`}>
    <div className="mx-auto max-w-7xl">{children}</div>
  </section>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const links = [
    ["Programs", "#programs"], ["About", "#about"], ["Tuition", "#tuition"],
    ["FAQ", "#faq"], ["Careers", "/careers"], ["Contact", "#contact"],
  ];  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-border/60">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
          <span className="h-11 w-11 rounded-2xl bg-[#3B82F6] grid place-items-center text-white font-display text-lg">
            TPL
          </span>
          <span className="leading-tight">
            <span className="block font-display font-bold text-lg">Teach Play to Learn</span>
            <span className="block text-xs uppercase tracking-[0.18em] text-muted-foreground">Little Rock, AR</span>
          </span>
        </Link>
        <nav className="hidden lg:flex items-center gap-8 text-sm font-semibold">
          {links.map(([l, h]) => (
            <a key={l} href={h} className="hover:text-[#3B82F6] transition-colors duration-200"
               data-testid={`nav-${l.toLowerCase()}`}>{l}</a>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/login"><Button variant="ghost" className="rounded-full font-bold" data-testid="header-login-btn">Log in</Button></Link>
          <Link to="/apply">
            <Button className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold px-6 hover:-translate-y-0.5 active:scale-95"
                    style={{ transition: "transform .2s ease, background-color .2s ease" }} data-testid="header-apply-btn">
              Apply Now
            </Button>
          </Link>
        </div>
        <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-border bg-white px-6 py-5 space-y-4" data-testid="mobile-menu">
          {links.map(([l, h]) => (
            <a key={l} href={h} onClick={() => setOpen(false)} className="block font-semibold">{l}</a>
          ))}
          <div className="flex gap-3 pt-2">
            <Link to="/login" className="flex-1"><Button variant="outline" className="w-full rounded-full">Log in</Button></Link>
            <Link to="/apply" className="flex-1"><Button className="w-full rounded-full bg-[#3B82F6]">Apply</Button></Link>
          </div>
        </div>
      )}
    </header>
  );
};

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", children: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", { ...form, role: "parent" });
      toast.success("Thanks! We'll reach out shortly.");
      setForm({ name: "", email: "", phone: "", children: "", message: "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
    setBusy(false);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-border p-8 space-y-4 shadow-sm"
          data-testid="contact-form">
      <h3 className="font-display text-xl sm:text-2xl font-semibold">Request a tour or a quote</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input required placeholder="Your name" value={form.name} onChange={set("name")}
               className="rounded-xl h-12" data-testid="contact-name" />
        <Input required type="email" placeholder="Email" value={form.email} onChange={set("email")}
               className="rounded-xl h-12" data-testid="contact-email" />
        <Input placeholder="Phone" value={form.phone} onChange={set("phone")}
               className="rounded-xl h-12" data-testid="contact-phone" />
        <Input placeholder="Children & ages" value={form.children} onChange={set("children")}
               className="rounded-xl h-12" data-testid="contact-children" />
      </div>
      <Textarea placeholder="What do you need help with?" value={form.message} onChange={set("message")}
                className="rounded-xl min-h-28" data-testid="contact-message" />
      <Button type="submit" disabled={busy}
              className="w-full h-12 rounded-full bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-bold active:scale-95"
              style={{ transition: "transform .2s ease, background-color .2s ease" }} data-testid="contact-submit">
        {busy ? "Sending…" : "Send message"}
      </Button>
    </form>
  );
};

export default function Home() {
  const [site, setSite] = useState({});
  const [previewAuthorized, setPreviewAuthorized] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const embeddedPreview = window.self !== window.top && new URLSearchParams(window.location.search).get("site_preview") === "1";
  useEffect(() => {
    document.title = "Teach Play to Learn — Childcare & Preschool Academy | Little Rock, AR";
    if (embeddedPreview) {
      api.get("/site-content/editor").then(({ data }) => {
        setSite(data.published); setPreviewAuthorized(true);
        window.parent.postMessage({ type: "website-preview-ready" }, window.location.origin);
      }).catch(() => {});
    } else api.get("/site-content/home").then(({ data }) => setSite(data)).catch(() => {});
  }, [embeddedPreview]);
  useEffect(() => {
    if (!previewAuthorized) return undefined;
    const receive = (event) => {
      if (event.origin !== window.location.origin || event.source !== window.parent || event.data?.type !== "website-preview-content") return;
      setSite(event.data.content || {}); setEditMode(Boolean(event.data.edit));
    };
    window.addEventListener("message", receive);
    window.parent.postMessage({ type: "website-preview-ready" }, window.location.origin);
    return () => window.removeEventListener("message", receive);
  }, [previewAuthorized]);
  const phoneHref = `tel:${(site.phone || "5019047103").replace(/\D/g, "")}`;
  const selectField = (event) => {
    if (!editMode) return;
    event.preventDefault(); event.stopPropagation();
    const field = event.target.closest("[data-site-field]")?.dataset.siteField;
    if (field) window.parent.postMessage({ type: "website-select-field", field }, window.location.origin);
  };
  if (site.unpublished && !embeddedPreview) return <div className="min-h-screen grid place-items-center p-8"><div><h1 className="text-3xl font-bold">Website temporarily unavailable</h1><p className="mt-3">Please check back soon.</p><Link className="block mt-5 text-blue-600 underline" to="/login">Family and staff sign-in</Link></div></div>;

  return (
    <div className={`min-h-screen bg-background overflow-x-hidden ${editMode ? "website-edit-preview" : ""}`} onClickCapture={selectField} onSubmitCapture={(event) => { if (embeddedPreview) { event.preventDefault(); event.stopPropagation(); } }}>
      {editMode && <style>{".website-edit-preview [data-site-field] {outline: 2px dashed #3B82F6; outline-offset: 4px; cursor: pointer;}"}</style>}
      <Header />

      {/* HERO */}
      <div className="relative">
        <div className="tpl-blob w-[420px] h-[420px] bg-blue-200/50 -top-24 -left-24" />
        <div className="tpl-blob w-[360px] h-[360px] bg-amber-200/50 top-40 right-0" />
        <Section className="relative">
          <div className="grid lg:grid-cols-[1.05fr,0.95fr] gap-14 lg:gap-20 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-flex items-center gap-2 rounded-full bg-white border border-border px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold text-[#3B82F6]">
                <Sparkle size={14} weight="fill" /> <span data-site-field="hero_badge">{site.hero_badge || "Now Enrolling · Ages 0–12"}</span>
              </span>
              <h1 className="mt-7 text-5xl sm:text-6xl tracking-tight font-bold leading-[1.05]">
                <span data-site-field="hero_title">{!site.hero_title || site.hero_title === "Promoting a bright future with early learning." ? <>Promoting a bright future with <span className="text-[#3B82F6]">early learning</span>.</> : site.hero_title}</span>
              </h1>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground max-w-xl">
                <span data-site-field="hero_body">{site.hero_body || "Teach Play to Learn Childcare Development Center, in conjunction with Hands That Help Pediatric Therapy, strengthens families through educational opportunities for children and their families — right here in Little Rock, Arkansas."}</span>
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link data-site-field="primary_button_label" to={site.primary_button_link || "/apply"}>
                  <Button className="h-14 px-8 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] text-base font-bold hover:-translate-y-0.5 active:scale-95"
                          style={{ transition: "transform .2s ease, background-color .2s ease" }} data-testid="hero-apply-btn">
                    {site.primary_button_label || "Apply Now"} <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <a href={phoneHref}>
                  <Button variant="outline" className="h-14 px-8 rounded-full border-2 text-base font-bold" data-testid="hero-call-btn">
                    <Phone size={18} className="mr-2" /> {site.phone || "(501) 904-7103"}
                  </Button>
                </a>
              </div>
              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm font-semibold text-muted-foreground">
                {["AR Certified Teachers", "USDA Meals Included", "Vouchers Accepted"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-2">
                    <CheckCircle size={17} weight="fill" className="text-[#10B981]" /> {t}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 0.15 }} className="relative">
              <img src={IMG.hero} alt="Children learning and playing at Teach Play to Learn"
                   className="rounded-[2rem] w-full h-[380px] sm:h-[480px] object-cover shadow-md" />
              <div className="absolute -bottom-8 left-4 sm:-left-8 bg-white rounded-3xl border border-border shadow-md p-6 max-w-[16rem]">
                <p className="font-display text-3xl font-bold text-[#3B82F6]">10+</p>
                <p className="text-sm text-muted-foreground mt-1 font-semibold">
                  Arkansas communities served, from Bryant to Wrightsville
                </p>
              </div>
            </motion.div>
          </div>
        </Section>
      </div>

      {/* PLAY LEARN GROW */}
      <Section className="bg-white border-y border-border" id="about">
        <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#EF4444]">Play. Learn. Grow.</p>
        <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight font-semibold max-w-2xl">
          Mastering the basics — cognitive, language, motor, social and behavior skills.
        </h2>
        <div className="mt-14 grid md:grid-cols-3 gap-8 lg:gap-12">
          {[
            { t: "Play.", h: "Our Mission", d: "Committed to strengthening families through the provision of educational opportunities for children and their families." },
            { t: "Learn.", h: "Our Goal", d: "Empower children and families with the knowledge and experience necessary to succeed in all possible futures — in inclusive, holistic learning environments." },
            { t: "Grow.", h: "Our Communities", d: "Bryant, College Station, Hensley, Jacksonville, Little Rock, North Little Rock, Sherwood, Sweet Home, Woodson and Wrightsville." },
          ].map((c, i) => (
            <motion.div key={c.t} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.12 }}
                        className="rounded-3xl border border-border p-8 hover:-translate-y-1 hover:shadow-md"
                        style={{ transition: "transform .25s ease, box-shadow .25s ease" }}>
              <p className="font-display text-4xl font-bold text-[#FBBF24]">{c.t}</p>
              <p className="mt-4 text-sm uppercase tracking-[0.2em] font-bold text-muted-foreground">{c.h}</p>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* PROGRAMS */}
      <Section id="programs">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Our Programs</p>
            <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight font-semibold max-w-2xl">
              Care and curriculum for every age, from infants to twelve.
            </h2>
          </div>
          <img src={IMG.learning} alt="Classroom learning"
               className="rounded-3xl w-full lg:w-72 h-40 object-cover shadow-sm" />
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {programs.map((p, i) => (
            <motion.div key={p.title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }} transition={{ duration: 0.45, delay: (i % 3) * 0.1 }}
                        className="bg-white rounded-3xl border border-border p-8 hover:-translate-y-1 hover:shadow-md"
                        style={{ transition: "transform .25s ease, box-shadow .25s ease" }}
                        data-testid={`program-card-${i}`}>
              <span className={`h-14 w-14 rounded-2xl grid place-items-center ${p.color}`}>
                <p.icon size={28} weight="duotone" />
              </span>
              <h3 className="mt-6 text-xl sm:text-2xl font-semibold">{p.title}</h3>
              <p className="mt-1 text-sm font-bold uppercase tracking-[0.15em] text-[#3B82F6]">{p.age}</p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* PARENT APP */}
      <Section className="bg-[#0F172A] text-white">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <img src={IMG.parentApp} alt="Parent using the Teach Play to Learn portal"
               className="rounded-[2rem] h-[360px] w-full object-cover" />
          <div>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#FBBF24]">Family Portal</p>
            <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight font-semibold">
              Your child's whole day, in your pocket.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-white/70">
              Every family gets a secure portal. See check-in and check-out times, meals, naps, learning notes and
              daily reports — then pay tuition with a card in two taps.
            </p>
            <ul className="mt-8 space-y-4">
              {["Live check-in/out and daily activity feed", "Daily reports for each child",
                "Digital enrollment application with signature", "Online tuition payments and receipts"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-base">
                  <CheckCircle size={22} weight="fill" className="text-[#FBBF24] shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/login">
              <Button className="mt-9 h-13 px-8 py-3 rounded-full bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-bold active:scale-95"
                      style={{ transition: "transform .2s ease, background-color .2s ease" }} data-testid="portal-login-btn">
                Open the portal
              </Button>
            </Link>
          </div>
        </div>
      </Section>

      {/* TUITION */}
      <Section id="tuition" className="bg-white border-y border-border">
        <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#EF4444]">Enrollment</p>
        <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight font-semibold">
          <span data-site-field="tuition_heading">{site.tuition_heading || "No application fee. Enrolling year-round."}</span>
        </h2>
        <p className="mt-4 text-base text-muted-foreground max-w-2xl">
          <span data-site-field="tuition_body">{site.tuition_body || "Tuition is priced individually based on the care your family needs. Vouchers and all other forms of payment are accepted — and there is never an application fee to get started."}</span>
        </p>
        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          {[
            { t: "No application fee", d: "Applying costs nothing. Send the form and we'll follow up to schedule your tour." },
            { t: "Always enrolling", d: "We accept applications year-round for every program, from infants through age twelve." },
            { t: "Vouchers welcome", d: "Vouchers, subsidies, cards, cash and checks are all accepted forms of payment." },
          ].map((c, i) => (
            <div key={c.t} className="rounded-3xl border-2 border-border p-8 hover:border-[#3B82F6] hover:-translate-y-1"
                 style={{ transition: "transform .25s ease, border-color .25s ease" }}
                 data-testid={`enrollment-card-${i}`}>
              <CheckCircle size={28} weight="fill" className="text-[#10B981]" />
              <p className="mt-5 font-display text-xl font-semibold">{c.t}</p>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>
        <Link to="/apply">
          <Button className="mt-10 h-14 px-8 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] text-base font-bold hover:-translate-y-0.5 active:scale-95"
                  style={{ transition: "transform .2s ease, background-color .2s ease" }}
                  data-testid="tuition-apply-btn">
            Start your application <ArrowRight size={18} className="ml-2" />
          </Button>
        </Link>
      </Section>

      {/* FAQ */}
      <Section id="faq">
        <div className="grid lg:grid-cols-[0.8fr,1.2fr] gap-14">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Daycare FAQs</p>
            <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight font-semibold">Questions parents ask us most.</h2>
            <img src={IMG.outdoor} alt="Children playing outdoors"
                 className="mt-10 rounded-3xl h-56 w-full object-cover hidden lg:block" />
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`i${i}`} className="border-b border-border">
                <AccordionTrigger className="text-left font-display text-lg font-semibold py-6"
                                  data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* CONTACT */}
      <Section id="contact" className="bg-white border-t border-border">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#EF4444]">Contact</p>
            <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight font-semibold">Come see the academy.</h2>
            <div className="mt-10 space-y-6">
              {[
                { icon: Phone, label: "Call", value: site.phone || "(501) 904-7103", href: phoneHref },
                { icon: Phone, label: "Text", value: site.text_phone || "(501) 791-1919", href: `sms:${(site.text_phone || "5017911919").replace(/\D/g, "")}` },
                { icon: EnvelopeSimple, label: "Email", value: site.public_email || "pprekinder@gmail.com", href: `mailto:${site.public_email || "pprekinder@gmail.com"}` },
                { icon: MapPin, label: "Location", value: site.location || "Little Rock, Arkansas" },
                { icon: Clock, label: "Hours", value: site.hours || "Mon–Fri 6:00 AM – 6:30 PM · Closed weekends" },
              ].map((c) => (
                <div key={c.label + c.value} data-site-field={{ Call: "phone", Text: "text_phone", Email: "public_email", Location: "location", Hours: "hours" }[c.label]} className="flex items-start gap-4">
                  <span className="h-11 w-11 rounded-2xl bg-blue-50 text-[#3B82F6] grid place-items-center shrink-0">
                    <c.icon size={20} weight="duotone" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} className="text-base font-semibold hover:text-[#3B82F6]"
                         style={{ transition: "color .2s ease" }}>{c.value}</a>
                    ) : (
                      <p className="text-base font-semibold">{c.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <ContactForm />
        </div>
      </Section>

      <footer className="bg-[#0F172A] text-white/70 px-6 sm:px-10 lg:px-16 py-14">
        <div className="mx-auto max-w-7xl flex flex-col md:flex-row gap-6 justify-between items-start md:items-center">
          <div>
            <p data-site-field="footer_text" className="font-display text-white text-lg font-bold">{site.footer_text || "Teach Play to Learn Childcare & Preschool Academy"}</p>
            <p className="text-sm mt-2">With Hands That Help Pediatric Therapy · Little Rock, AR</p>
          </div>
          <div className="flex gap-6 text-sm font-semibold">
            <Link to="/apply" className="hover:text-[#FBBF24]" style={{ transition: "color .2s ease" }}>Apply</Link>
            <Link to="/careers" className="hover:text-[#FBBF24]" style={{ transition: "color .2s ease" }} data-testid="footer-careers">Careers</Link>
            <Link to="/login" className="hover:text-[#FBBF24]" style={{ transition: "color .2s ease" }}>Portal</Link>
            <a href="#contact" className="hover:text-[#FBBF24]" style={{ transition: "color .2s ease" }}>Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
